import * as sizeManager from "@/modules/sizeManager";
import config from "@/modules/config";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import * as dimensionUtils from "@/common/utils/dimensionUtils";
import type { Size } from "@/common/utils/dimensionUtils";
import type { Edges, TemplateSizes } from "@/types/layout";

const CONTROL_BAR_SIZE = 55.0;

let padding: Edges = { left: 0, right: 0, bottom: 0, top: 0 };
let showPanel = true;
let showControlBar = false;
let centered = false;
let lastSize: Size | null = null;
let sizes: TemplateSizes | null = null;

// padding is the padding around the player floating window.
export function setPadding(next: Edges) {
  if (padding !== next) {
    padding = next;
    sizes = null;
  }
}

export function setCentered(value: boolean) {
  if (centered !== value) {
    centered = value;
    sizes = null;
  }
}

export function setShowPanel(value: boolean) {
  if (showPanel !== value) {
    showPanel = value;
    sizes = null;
  }
}

export function setShowControlBar(value: boolean) {
  if (showControlBar !== value) {
    showControlBar = value;
    sizes = null;
  }
}

export function getSizes(size?: Size | null): TemplateSizes {
  if (!size) {
    size = sizeManager.getSize();
  }

  const availableSize = {} as Size;
  availableSize.width = size.width - padding.left - padding.right;
  availableSize.height = size.height - padding.top - padding.bottom;

  if (!dimensionUtils.sizeEquals(size, lastSize)) {
    sizes = null;
    lastSize = size;
  }

  if (sizes == null) {
    // Phone-scale landscape is the video-first immersive layout: a
    // full-bleed 16:9 box, no side panel, controls overlaid. Larger (desktop)
    // landscape keeps the original video + shop-window split geometry.
    // Viewport-based (see orientationManager.isImmersiveLandscapeSize) so it
    // matches Experience.js / MainWindow.js and the DevTools device toolbar.
    sizes = isImmersiveLandscapeSize(size)
      ? immersiveSizes(availableSize)
      : splitSizes(size, availableSize);
  }

  return sizes;
}

// Immersive fills the ENTIRE viewport — no centred 16:9 box, no margins, so
// there are never letterbox "white fields" around the player. The video is
// fitted inside this full-bleed (black) box by CSS object-fit, so any aspect
// mismatch shows as black bars within the edge-to-edge surface, never the
// page background. Panel width 0 (no side column).
function immersiveSizes(availableSize: Size): TemplateSizes {
  const containerPadding = { left: 0, right: 0, bottom: 0, top: 0 };
  const containerWidth = availableSize.width;
  const containerHeight = availableSize.height;

  return {
    player: {
      padding: containerPadding,
      container: {
        width: containerWidth,
        height: containerHeight,
        margins: { top: padding.top, left: padding.left },
      },
      video: {
        width: containerWidth,
        height: containerHeight,
        margins: {
          left: containerPadding.left,
          top: containerPadding.top,
          right: 0,
          bottom: containerPadding.bottom,
        },
      },
      panel: {
        width: 0,
        height: containerHeight,
        margins: {
          left: 0,
          top: containerPadding.top,
          right: 0,
          bottom: containerPadding.bottom,
        },
      },
    },
    favorites: {
      lightbox: {
        width: containerWidth,
        height: containerHeight,
        margins: containerPadding,
      },
    },
  };
}

// Original desktop landscape: video (75%) + shop-window panel (25%),
// optionally reserving a control-bar column, centred as a floating window.
function splitSizes(size: Size, availableSize: Size): TemplateSizes {
  const containerPadding = { left: 10, right: 10, bottom: 10, top: 10 };
  const containerSpacing = 8;
  const controlBarWidth = showControlBar ? CONTROL_BAR_SIZE : 0;
  const controlBarWidthWithSpacing = showControlBar
    ? controlBarWidth + containerSpacing
    : 0;

  // The combined width of the video and shop window
  const workWidth =
    availableSize.width -
    containerPadding.left -
    containerPadding.right -
    containerSpacing -
    controlBarWidthWithSpacing;
  let videoWidth = workWidth * 0.75;
  let panelWidth = workWidth * 0.25;
  let videoHeight = (videoWidth / 16) * 9;
  let containerWidth = availableSize.width;
  let containerHeight =
    videoHeight + containerPadding.top + containerPadding.bottom;

  // The aspect ratio is dynamic so we prettymuch calculate the height
  // based on the width. If it's too tall then we calculate the width based on
  // the height.
  if (containerHeight > availableSize.height) {
    containerHeight = availableSize.height;
    videoHeight =
      containerHeight - containerPadding.top - containerPadding.bottom;
    videoWidth = (videoHeight * 16) / 9;
    panelWidth = (videoWidth / 0.75) * 0.25;
    containerWidth =
      videoWidth +
      panelWidth +
      containerPadding.left +
      containerPadding.right +
      containerSpacing +
      controlBarWidthWithSpacing;
  }

  let marginTop = centered
    ? (size.height - containerHeight) / 2.0
    : padding.top;
  let marginLeft = centered
    ? (size.width - containerWidth) / 2.0
    : padding.left;

  if (centered) {
    if (marginTop < padding.top) {
      marginTop = padding.top;
    }

    if (marginLeft < padding.left) {
      marginLeft = padding.left;
    }
  }

  return {
    player: {
      padding: containerPadding,
      container: {
        width: containerWidth,
        height: containerHeight,
        margins: { top: marginTop, left: marginLeft },
      },
      video: {
        width: videoWidth,
        height: videoHeight,
        margins: {
          left: containerPadding.left,
          top: containerPadding.top,
          right: 0,
          bottom: containerPadding.bottom,
        },
      },
      panel: {
        width: panelWidth,
        height: videoHeight,
        margins: {
          left: containerSpacing,
          top: containerPadding.top,
          right: showControlBar ? containerSpacing : 0,
          bottom: containerPadding.bottom,
        },
      },
    },
    favorites: {
      lightbox: {
        width: videoWidth,
        height: videoHeight,
        margins: containerPadding,
      },
    },
  };
}

if (config.shownAs()) {
  setPadding({ left: 20, right: 20, top: 50, bottom: 10 });
}
setCentered(true);
